import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextField } from 'components/Field/TextField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useSelector } from 'hooks/useSelector';
import { cloneDeep, isEqual, sortBy } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  eventFieldSettingUpdateGroupCheck,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { selectFieldSettingsGroupCheckTypes } from 'store/remote/selectors';
import { createId } from '@paralleldrive/cuid2';
import { PageHeader } from 'components/PageHeader';

export const FieldSettingsGroupCheckModal: React.FC<
  ModalScreenProps<ModalScreens.FieldSettingsGroupCheck>
> = ({ route, navigation }) => {
  const { eventKey, data } = route.params;
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();

  const checkTypes = useSelector(selectFieldSettingsGroupCheckTypes);
  const orderedCheckTypes = sortBy(Object.values(checkTypes), 'ordering');

  const dataRef = React.useRef({ ...data });
  const initDataRef = React.useRef(cloneDeep(dataRef.current));

  const checkType = checkTypes[dataRef.current.typeId] || orderedCheckTypes[0]!;

  const isDirtyRef = React.useRef(false);
  const checkIsDirty = React.useCallback(
    () => !isEqual(initDataRef.current, dataRef.current),
    [],
  );
  isDirtyRef.current = checkIsDirty();

  const handleHardSave = React.useCallback(() => {
    if (!initDataRef.current.id || checkIsDirty()) {
      dispatch(
        eventFieldSettingUpdateGroupCheck({
          key: eventKey,
          data: {
            ...dataRef.current,
            id: dataRef.current.id || createId(),
            name:
              dataRef.current.name ||
              initDataRef.current.name ||
              checkType.name,
            action: !dataRef.current.id ? 'create' : dataRef.current.action,
          },
        }),
      );
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [checkIsDirty, checkType.name, dispatch, eventKey]);

  const handleClose = React.useCallback(() => {
    if (checkIsDirty()) {
      confirmUnsaved({
        onSave: handleHardSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [checkIsDirty, confirmUnsaved, dispatch, handleHardSave]);

  const handleSoftSave = React.useCallback(
    (
      changes: Partial<typeof data>,
      { doForceUpdate }: { doForceUpdate?: 'always' | 'dirtyChange' },
    ) => {
      const newValues = { ...dataRef.current, ...changes };
      if (!isEqual(dataRef.current, newValues)) {
        dataRef.current = newValues;
        if (
          doForceUpdate === 'always' ||
          (doForceUpdate === 'dirtyChange' &&
            checkIsDirty() !== isDirtyRef.current)
        ) {
          forceUpdate();
        }
      }
    },
    [checkIsDirty, forceUpdate],
  );

  const isDirtyVar = checkIsDirty();
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Check"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleHardSave}
        />
      }
    >
      <Card>
        <TextField
          placeholder={`Name (Default: ${checkType.name})`}
          value={dataRef.current.name}
          onChange={(value) =>
            handleSoftSave(
              {
                name: value,
              },
              { doForceUpdate: 'dirtyChange' },
            )
          }
          onEsc={handleClose}
          autoFocusWeb
          autoFocusDelay
        />
      </Card>

      {dataRef.current.action === 'create' ? (
        <Card
          label="Check Frequency"
          explanation="Frequency cannot be changed after this check is created"
        >
          {orderedCheckTypes.map((ct) => (
            <CardRow
              labelAlignTop
              label={ct.name}
              key={ct.id}
              radioValue={dataRef.current.typeId === ct.id}
              onPress={() =>
                handleSoftSave({ typeId: ct.id }, { doForceUpdate: 'always' })
              }
            />
          ))}
        </Card>
      ) : (
        <Card label="Check Frequency">
          <CardRow value={checkType.name} />
        </Card>
      )}
    </Page>
  );
};
